import { useState } from 'react'
import { CatalogListTemplate } from '../catalog.types'
import { IRecordWithStrings } from 'core/helpers'
import { IBreadcrumbsItem } from 'core/models/Breadcrumbs'

interface IBreadcrumbsSelectedItem {
    id: number
}

type BreadcrumbsTemplate = Pick<
    CatalogListTemplate,
    'isEqual' | 'isFolder' | 'multipleMove' | 'moveFolder' | 'moveItem'
>

export interface IBreadcrumbsProps {
    list: IBreadcrumbsItem[]
    multipleSelect: boolean
    selectedItems: IBreadcrumbsSelectedItem[]
    onResetSelection: () => void
    template: BreadcrumbsTemplate
    filterName: string
    type: string
}

export interface IBreadcrumbsItemProps extends IBreadcrumbsProps {
    listItemData: {
        index: number
        breadcrumb: IBreadcrumbsItem
        pathname: string
    }
    query: IRecordWithStrings
}

interface IDroppedItem {
    id: number
    [key: string]: unknown
}

const isDroppedItem = (value: unknown): value is IDroppedItem =>
    typeof value === 'object' && value !== null && typeof (value as { id?: unknown }).id === 'number'

const CatalogBreadcrumbsMethods = (p_: IBreadcrumbsItemProps) => {
    const [isHovered, setIsHovered] = useState(false)

    function onDrop(data: { dnd_item: string }, event: React.DragEvent) {
        event.stopPropagation()

        const droppedItem: unknown = JSON.parse(data.dnd_item)

        if (!isDroppedItem(droppedItem)) {
            setIsHovered(false)
            return
        }

        const maxBreadcrumbDepth = p_.list.length - 1
        const currentBreadcrumbDepth = p_.listItemData.index

        if (currentBreadcrumbDepth === maxBreadcrumbDepth) {
            setIsHovered(false)
            return
        }

        const breadcrumb = p_.listItemData.breadcrumb

        if (!breadcrumb.id) {
            setIsHovered(false)
            return
        }

        if (p_.template.multipleMove && p_.multipleSelect && p_.selectedItems.length) {
            const droppedItemInSelected = p_.selectedItems.some((selectedItem) =>
                p_.template.isEqual(selectedItem, droppedItem)
            )

            if (droppedItemInSelected) {
                onMoveMultiple()
                return
            }
        }

        const isDroppedFolder = p_.template.isFolder(droppedItem)

        if (isDroppedFolder && droppedItem.id === breadcrumb.id) {
            setIsHovered(false)
            return
        }

        const moveMethod = isDroppedFolder ? p_.template.moveFolder : p_.template.moveItem

        moveMethod(droppedItem.id, breadcrumb.id)
        setIsHovered(false)
    }

    function onMoveMultiple() {
        const multipleMove = p_.template.multipleMove
        const newFolderId = p_.listItemData.breadcrumb.id

        if (!multipleMove || !newFolderId) {
            setIsHovered(false)
            return
        }

        const sourceIds: number[] = []
        const folderIds: number[] = []

        p_.selectedItems.forEach((item) => {
            const isFolder = p_.template.isFolder(item)

            if (isFolder && item.id === newFolderId) {
                return
            }

            isFolder ? folderIds.push(item.id) : sourceIds.push(item.id)
        })

        multipleMove(sourceIds, folderIds, newFolderId).then(p_.onResetSelection)
        setIsHovered(false)
    }

    function onDragOver() {
        if (!isHovered) {
            setIsHovered(true)
        }
    }

    function onDragLeave() {
        if (isHovered) {
            setIsHovered(false)
        }
    }

    return {
        s_: {
            isHovered,
        },
        methods: {
            onDrop,
            onDragOver,
            onDragLeave,
        },
    }
}

export default CatalogBreadcrumbsMethods
